<template>
  <div v-if="hasAnyPermission('data-log-view')" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div class="card-header py-3">
        <div class="d-flex flex-wrap align-items-center gap-3">
          <h5 class="mb-0 fw-bold">Data Logs</h5>

          <div class="d-flex flex-wrap gap-2 flex-grow-1 justify-content-center">
            <IconField class="search-field">
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText v-model="table.search" type="text" size="small" placeholder="Search Data Log"
                class="rounded-pill" style="min-width: 500px;" @keyup="filter" />
            </IconField>
          </div>

          <div class="d-flex flex-wrap gap-2">
            <button type="button" class="btn btn-sm btn-outline-primary m-0" @click="openFilterDialog">
              <i class="pi pi-filter me-1"></i>
              Filter
            </button>
            <button v-if="hasAnyPermission('data-log-export')" type="button" class="btn btn-sm btn-primary m-0"
              @click="exportToExcel">
              <i class="pi pi-download me-1"></i>
              Export
            </button>
          </div>
        </div>
      </div>
      <div style="height: 500px; overflow-y: auto;">
        <DataTable :value="table.onLoad ? dummyRows : table.data" removable-sort scrollable scroll-height="flex"
          table-style="min-width: 50rem">
          <Column field="user_type" header="User Type" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.user_type ? formatUserType(slotProps.data.user_type) : '' }}
            </template>
          </Column>
          <Column field="user_id" header="User ID" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.user_id ? slotProps.data.user_id : '' }}
            </template>
          </Column>
          <Column field="event" header="Event" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              <span v-if="slotProps.data && slotProps.data.event" :class="getEventBadgeClass(slotProps.data.event)">
                {{ slotProps.data.event }}
              </span>
            </template>
          </Column>
          <Column field="auditable_type" header="Model Type" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.auditable_type ? formatModelType(slotProps.data.auditable_type) : ''
              }}
            </template>
          </Column>
          <Column field="auditable_id" header="Model ID" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.auditable_id ? slotProps.data.auditable_id : '' }}
            </template>
          </Column>
          <Column field="url" header="URL" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              <div class="text-truncate" style="max-width: 200px;" :title="slotProps.data?.url">
                {{ slotProps.data && slotProps.data.url ? slotProps.data.url : '' }}
              </div>
            </template>
          </Column>
          <Column field="ip_address" header="IP Address" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.ip_address ? slotProps.data.ip_address : '' }}
            </template>
          </Column>
          <Column field="created_at" header="Created At" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.created_at ? formatDate(slotProps.data.created_at) : '' }}
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">
                No Data
              </div>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination v-model="table.page.current_pages" :page-count="table.page?.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_pages - 1) * table.page.per_pages + table.data.length }} of {{ table.total }}
        </p>
      </div>
    </div>

    <DialogPrime v-model:visible="filterDialog.visible" modal style="width: 30rem; margin: 10px;">
      <template #header>
        <h5 class="mb-0 fw-bold text-dark">Filter</h5>
      </template>
      <div class="row g-3">
        <div class="col-6">
          <label for="filter-created-start" class="form-label fw-semibold">Created At Start</label>
          <Calendar id="filter-created-start" v-model="filterDialog.form.created_at_start" date-format="yy-mm-dd"
            show-icon placeholder="YYYY-MM-DD" class="w-100" />
        </div>
        <div class="col-6">
          <label for="filter-created-end" class="form-label fw-semibold">Created At End</label>
          <Calendar id="filter-created-end" v-model="filterDialog.form.created_at_end" date-format="yy-mm-dd" show-icon
            placeholder="YYYY-MM-DD" class="w-100" />
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Reset" severity="danger" raised size="small" class="px-4 border-0"
            @click="resetFilter()" />
          <ButtonPrime type="button" severity="contrast" raised size="small" label="Apply Filter" class="px-4"
            @click="applyFilter()" />
        </div>
      </template>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import BasePagination from "@/components/BasePagination.vue";
import moment from "moment";
import api from "@/helpers/api";
import dataLogService from "@/services/report/appAuditing/dataLogService";
import NoAccess from "@/components/NoAccess.vue";

export default {
  components: {
    BasePagination,
    NoAccess,
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
  },
  data() {
    return {
      table: {
        onLoad: true,
        search: this.$route.query.search || "",
        total: 0,
        data: [],
        limit: 100,
        page: {
          current_pages: 1,
          per_pages: 5,
          total_pages: 1,
        },
        loadTimeout: null,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      filterDialog: {
        visible: false,
        form: {
          created_at_start: this.$route.query.created_at_start || null,
          created_at_end: this.$route.query.created_at_end || null,
        }
      },
    };
  },
  mounted() {
    if (this.hasAnyPermission('data-log-view')) {
      this.get();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    toYMD(val) {
      if (!val) return "";
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const d = val instanceof Date ? val : new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return moment(d).format("YYYY-MM-DD");
    },
    openFilterDialog() {
      this.filterDialog.visible = true;
    },
    resetFilter() {
      this.filterDialog.form = { created_at_start: null, created_at_end: null };
      this.$router.push({ path: this.$route.path, query: this.table.search ? { search: this.table.search } : null });
      this.table.page.current_pages = 1;
      this.get();
    },
    applyFilter() {
      const qStart = this.toYMD(this.filterDialog.form.created_at_start);
      const qEnd = this.toYMD(this.filterDialog.form.created_at_end);
      const query = {
        ...(this.table.search ? { search: this.table.search } : {}),
        ...(qStart ? { created_at_start: qStart } : {}),
        ...(qEnd ? { created_at_end: qEnd } : {}),
      };
      this.$router.push({ path: this.$route.path, query: Object.keys(query).length ? query : null });
      this.table.page.current_pages = 1;
      this.filterDialog.visible = false;
      this.get();
    },
    async get() {
      this.table.onLoad = true;
      try {
        const params = {
          search: this.table.search,
          limit: this.table.limit,
          page: this.table.page.current_pages,
        };
        const qStart = this.toYMD(this.$route.query.created_at_start);
        const qEnd = this.toYMD(this.$route.query.created_at_end);
        if (qStart) params.created_at_start = qStart;
        if (qEnd) params.created_at_end = qEnd;
        const response = await api(
          this,
          dataLogService.list(params),
          false
        );
        const paged = response.data || {};
        this.table.data = paged.data || [];
        this.table.total = paged.total || this.table.data.length;
        this.table.page.total_pages = paged.last_page || 1;
        this.table.page.current_pages = paged.current_page || 1;
        this.table.page.per_pages = paged.per_page || this.table.limit;
      } catch (error) {
        this.table.data = [];
        this.table.total = 0;
        this.table.page.total_pages = 1;
      } finally {
        this.table.onLoad = false;
      }
    },
    filter() {
      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        const qStart = this.toYMD(this.$route.query.created_at_start);
        const qEnd = this.toYMD(this.$route.query.created_at_end);
        const query = {
          ...(this.table.search ? { search: this.table.search } : {}),
          ...(qStart ? { created_at_start: qStart } : {}),
          ...(qEnd ? { created_at_end: qEnd } : {}),
        };
        this.$router.push({
          path: this.$route.path,
          query: Object.keys(query).length ? query : null,
        });
        this.table.page.current_pages = 1;
        this.get();
      }, 500);
    },
    changePage() {
      this.get();
    },
    formatDate(date) {
      return date ? moment(date).format("DD MMMM YYYY, HH:mm") : "-";
    },
    formatUserType(userType) {
      if (!userType) return '';
      // Extract class name from full namespace
      const parts = userType.split('\\');
      return parts[parts.length - 1] || userType;
    },
    formatModelType(modelType) {
      if (!modelType) return '';
      // Extract class name from full namespace
      const parts = modelType.split('\\');
      return parts[parts.length - 1] || modelType;
    },
    getEventBadgeClass(event) {
      const classes = {
        'created': 'badge badge-success',
        'updated': 'badge badge-warning',
        'deleted': 'badge badge-danger',
        'restored': 'badge badge-info'
      };
      return classes[event] || 'badge badge-secondary';
    },
    async exportToExcel(event) {
      const button = event.target;
      const originalText = button.innerHTML;

      try {
        button.disabled = true;
        button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>&nbsp;&nbsp;&nbsp;Exporting...';

        const response = await dataLogService.export();
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "data_logs.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error exporting data logs:", error);
        alert("Gagal mengekspor data. Silakan coba lagi.");
      } finally {
        button.disabled = false;
        button.innerHTML = originalText;
      }
    },
  },
};
</script>

<style scoped>
.badge {
  font-size: 0.75em;
  padding: 0.25em 0.5em;
}
</style>